import { r as registerInstance, h } from './index-BntG-4Sv.js';
import { ToastMessageList, ToastrConfig, WindowService, ToastType } from '@ontotext/workbench-api';
import { s as sanitizeHTML, T as TranslationService } from './translation.service-FB05Sh97.js';

const ontoToastrCss = ".onto-toastr-container{position:fixed;z-index:9999999;pointer-events:auto;flex-direction:column}.onto-toastr-container.bottom-right{bottom:12px;right:12px}.onto-toastr-container.bottom-left{bottom:12px;left:12px}.onto-toastr-container .onto-toast{display:flex;align-items:center;border-radius:0;box-shadow:0 0 12px rgba(0, 0, 0, 0.3);position:relative;width:400px;padding:25px 0;color:white;margin-bottom:5px;overflow-wrap:break-word}.onto-toastr-container .onto-toast:hover{box-shadow:0 0 12px #000;opacity:1;cursor:pointer}.onto-toastr-container .onto-toast.warning{color:var(--gw-toast-warn-color);background-color:var(--gw-toast-warn-background);border-color:var(--gw-toast-warn-border-color)}.onto-toastr-container .onto-toast.success{background-color:var(--gw-toast-success-background);border-color:var(--gw-toast-success-border-color);color:var(--gw-toast-success-color)}.onto-toastr-container .onto-toast.error{background-color:var(--gw-toast-error-background);border-color:var(--gw-toast-error-border-color);color:var(--gw-toast-error-color)}.onto-toastr-container .onto-toast.info{background-color:var(--gw-toast-info-background);border-color:var(--gw-toast-info-border-color);color:var(--gw-toast-info-color)}.onto-toastr-container .onto-toast i{font-size:1.8rem;padding:0 20px;color:inherit}.onto-toastr-container .onto-toast i:hover{transform:none}.onto-toastr-container .onto-toast .toast-message{font-size:1.2rem;opacity:1;max-width:370px}";

const toastTypeToIconMap = {
    [ToastType.INFO]: 'ri-information-2-fill',
    [ToastType.SUCCESS]: 'ri-checkbox-circle-line',
    [ToastType.WARNING]: 'ri-message-line',
    [ToastType.ERROR]: 'ri-alert-line',
};
const OntoToastr = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        /**
         * State containing the list of active toast messages
         */
        this.toasts = new ToastMessageList([]);
        /**
         * Map to track timeout IDs for each toast message
         */
        this.toastToTimeout = new Map();
        /** Configuration options, such as toast timeout */
        this.config = ToastrConfig.getDefaultConfig();
        this.onToastMouseEnter = (toast) => () => {
            this.clearToastTimeout(toast);
        };
        this.onToastMouseLeave = (toast) => () => {
            this.setTimeoutForToast(toast);
        };
    }
    /**
     * Listen for toast creation events
     *
     * @param event - Custom event containing the toast message to be displayed
     */
    // TODO Fix jest tests are failing- usage variables in stencil decorators is enabled since 4.3.0
    // see https://github.com/stenciljs/core/issues/2924
    handleToastCreate(event) {
        this.addToast(event.detail);
    }
    render() {
        var _a;
        return (h("section", { key: '21b2b0dd0f50aabbe2eed7ee6080eaf450c167e7', style: { display: this.toasts.isEmpty() ? 'none' : 'flex' }, class: `onto-toastr-container ${this.config.position}` }, (_a = this.toasts) === null || _a === void 0 ? void 0 : _a.getItems().map((toast) => {
            var _a, _b, _c;
            return (h("div", { class: `onto-toast toast ${toast.type}`, key: toast.id, onMouseEnter: this.onToastMouseEnter(toast), onMouseLeave: this.onToastMouseLeave(toast), onClick: this.handleToastClick(toast), onKeyUp: this.handleKeyUp(toast) }, h("i", { class: `${toastTypeToIconMap[toast.type]}` }), h("span", null, ((_a = toast.config) === null || _a === void 0 ? void 0 : _a.title) ?
                h("div", { class: "toast-title", innerHTML: sanitizeHTML(TranslationService.translate(toast.config.title, (_b = toast.config) === null || _b === void 0 ? void 0 : _b.translationParams)) }) : '', h("div", { class: "toast-message", innerHTML: sanitizeHTML(TranslationService.translate(toast.message, (_c = toast.config) === null || _c === void 0 ? void 0 : _c.translationParams)) }))));
        })));
    }
    /**
     * Adds a new toast message.
     * The message is added to the start of the list, so new toasts appear on top.
     * The toast is also set to automatically be removed after the specified timeout.
     *
     * @param toast - The toast message to be added
     */
    addToast(toast) {
        this.toasts.addToStart(toast);
        this.setTimeoutForToast(toast);
        this.updateToastrReference();
    }
    /**
     * Sets a timeout for automatic removal of a toast message.
     * The value of the timeout is determined by the toast's configuration, or the default timeout, if
     * the toast hasn't provided a value
     *
     * @param toast - The toast message to set timeout for
     */
    setTimeoutForToast(toast) {
        var _a;
        const timeout = WindowService.getWindow().setTimeout(() => {
            this.toasts.remove(toast);
            this.clearToastTimeout(toast);
        }, ((_a = toast.config) === null || _a === void 0 ? void 0 : _a.timeout) || this.config.timeout);
        this.toastToTimeout.set(toast, timeout);
    }
    /**
     * Updates the toast list reference to trigger re-rendering
     */
    updateToastrReference() {
        this.toasts = new ToastMessageList(this.toasts.getItems());
    }
    /**
     * Clears the timeout for a toast message and removes it from the tracking map
     * When a user hovers over a toast message, the timeout is cleared, since the toast should
     * remain visible until the user stops hovering.
     *
     * @param toast - The toast message whose timeout should be cleared
     */
    clearToastTimeout(toast) {
        const timeoutId = this.toastToTimeout.get(toast);
        clearTimeout(timeoutId);
        this.toastToTimeout.delete(toast);
        this.updateToastrReference();
    }
    /**
     * Handle click events on toast messages.
     * Executes the configured onClick callback for the toast message, if provided.
     * Removes the toast message, if the removeOnClick configuration is enabled.
     *
     * @param toast - The clicked toast message
     * @returns An event handler function that processes click events on the toast
     */
    handleToastClick(toast) {
        return (event) => {
            var _a, _b;
            if ((_a = toast.config) === null || _a === void 0 ? void 0 : _a.onClick) {
                toast.config.onClick(event);
            }
            if ((_b = toast.config) === null || _b === void 0 ? void 0 : _b.removeOnClick) {
                this.toasts.remove(toast);
                this.clearToastTimeout(toast);
            }
        };
    }
    handleKeyUp(toast) {
        return (event) => {
            if (event.key === 'Enter') {
                this.handleToastClick(toast);
            }
        };
    }
};
OntoToastr.style = ontoToastrCss;

export { OntoToastr as onto_toastr };
